import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import { ChatAssistantBubble, ChatWelcomeBubble } from "@/components/chat/primitives";
import { sanitizeLLMOutput } from "@/lib/sanitizeLlmOutput";
import { Copy, Timer, Languages, ChevronRight, CircleCheckBig } from "lucide-react";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import type { AddressChatRedirectMeta, AnswerReasoning, ChatMessage, MessageFeedbackState, MessageTranslation } from "@/types/chat";
import type { FeedbackEntryPoint } from "@/lib/responseFeedback";
import "@github/relative-time-element";
import { OrdinalIcon } from "../OrdinalIcon";
import FeedbackDialog from "../FeedbackDialog";
import FeedbackActions from "../FeedbackActions";
import { deleteMessageEvaluation, submitMessageEvaluation, submitStructuredMessageFeedback } from "@/api/messages";
import Reasoning from "./Reasoning";
import Citations from "./Citations";
import Contradictions from "./Contradictions";
import { ANSWER_REASONING_LABEL, AnswerReasoningPanel } from "./AnswerReasoningPanel";
// import ContradictionStatus from "./ContradictionStatus";
import { useToast } from "@/hooks/useToast";
import { useUser } from "@/context/UserProvider";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import type { ResearchSummary } from "./LoadingStates";
import {
  buildCitationDisplayNumbers,
  citationEntryKey,
  detectCitationMarkerStyle,
  normalizeCitationLinks,
  normalizeTranslationCitationLinks,
  type CitationEntry,
} from "./citationUtils";
import {
  buildMarkdownComponents,
  citationUrlTransform as urlTransform,
} from "./citationMarkdown";

// @ts-ignore - ActionCable types not available
import { createConsumer } from "@rails/actioncable";

interface BotMessageProps {
  message: ChatMessage;
  researchSummary?: ResearchSummary | null;
  onAddressChatRedirect?: (meta: AddressChatRedirectMeta) => void;
  onRegisterOpenAnswerReasoning?: (open: (() => Promise<void>) | null) => void;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function feedbackUiFromMessage(message: ChatMessage): {
  thumb_state: "helpful" | "unhelpful" | null;
  has_feedback: boolean;
} {
  const fs = message.feedback_state;
  const legacy = message.message_evaluation;
  const thumb = (fs?.thumb_state ?? legacy?.evaluation ?? null) as "helpful" | "unhelpful" | null;
  return {
    thumb_state: thumb,
    has_feedback: Boolean(fs?.has_feedback),
  };
}

/**
 * For legacy numeric translations on a direct parent, map stored citation:N
 * indices to the parent's References display numbers via entry keys.
 * Falls back to the local index map when the parent has no number for a key.
 */
function remapLegacyTranslationDisplayNumbers(
  citations: CitationEntry[] | undefined,
  localDisplayNumbers: Map<string, number>,
  parentDisplayNumbers?: Map<string, number>,
): Map<string, number> {
  if (!parentDisplayNumbers || !citations?.length) return localDisplayNumbers;

  const remapped = new Map(localDisplayNumbers);
  citations.forEach((entry, index) => {
    const key = citationEntryKey(entry);
    if (!key) return;
    const parentNumber = parentDisplayNumbers.get(key);
    if (parentNumber == null) return;
    remapped.set(String(index + 1), parentNumber);
    if (!remapped.has(key)) {
      remapped.set(key, parentNumber);
    }
  });
  return remapped;
}

export function BotWelcomeMessage() {
  return <ChatWelcomeBubble message="Hi, how can I help?" />;
}

// TranslationPill displays a single translation with a language label pill
function TranslationPill({
  translation,
  onCitationClick,
  totalCitations,
  citations,
  parentDisplayNumbers,
}: {
  translation: MessageTranslation;
  onCitationClick: (key: string, contentForOrdering?: string) => void;
  totalCitations: number;
  citations?: CitationEntry[];
  parentDisplayNumbers?: Map<string, number>;
}) {
  // Clean the translation text: remove code block markers if present and trim whitespace
  let translationText = sanitizeLLMOutput(translation.translation_text?.trim() || "");

  // Remove markdown code block markers (```language or ```) if the text is wrapped in them
  // This handles cases where the LLM returns the translation wrapped in code blocks
  // Remove leading code block marker (``` or ```language)
  translationText = translationText.replace(/^```[\w]*\n?/m, "");
  // Remove trailing code block marker (```)
  translationText = translationText.replace(/\n?```\s*$/m, "");
  // Also check for ``` at the end of any line and remove it
  translationText = translationText.replace(/```\s*$/gm, "");
  translationText = translationText.trim();
  const formattedTranslation = normalizeTranslationCitationLinks(
    translationText,
    totalCitations,
    citations,
  );
  const translationLabels = citations?.map(citationEntryKey).filter(Boolean);
  const translationMarkerStyle = detectCitationMarkerStyle(
    formattedTranslation,
    translationLabels,
  );
  // Direct translations share References row numbers with the parent answer.
  // Legacy translations remap stored indices through parent keys so badge
  // numbers still match References when the parent reordered by appearance.
  const localTranslationDisplayNumbers = buildCitationDisplayNumbers(
    citations,
    formattedTranslation,
  );
  const translationDisplayNumbers =
    translationMarkerStyle === "direct" && parentDisplayNumbers
      ? parentDisplayNumbers
      : remapLegacyTranslationDisplayNumbers(
          citations,
          localTranslationDisplayNumbers,
          parentDisplayNumbers,
        );
  const handleTranslationCitationClick = (key: string) => {
    onCitationClick(key, formattedTranslation);
  };

  return (
    <div className="mt-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 text-sm font-medium mb-2">
        <Languages className="h-3.5 w-3.5" />
        <span>{translation.target_language}</span>
      </div>
      <div className="prose dark:prose-invert prose-headings:m-0 prose-ol:m-0 prose-ul:m-0 prose-li:m-0 prose-p:m-0 max-w-none">
        <SafeMarkdown
          remarkPlugins={[remarkGfm]}
          components={buildMarkdownComponents(
            handleTranslationCitationClick,
            translationDisplayNumbers,
            translationMarkerStyle,
            citations,
          )}
          urlTransform={urlTransform}
        >
          {formattedTranslation}
        </SafeMarkdown>
      </div>
    </div>
  );
}

// TranslationsSection displays all translations for a message
function TranslationsSection({
  translations,
  onCitationClick,
  totalCitations,
  citations,
  parentDisplayNumbers,
}: {
  translations?: MessageTranslation[];
  onCitationClick: (key: string, contentForOrdering?: string) => void;
  totalCitations: number;
  citations?: CitationEntry[];
  parentDisplayNumbers?: Map<string, number>;
}) {
  // Defensive checks - ensure we have a valid array with items
  if (!translations || !Array.isArray(translations) || translations.length === 0) return null;

  return (
    <div className="mt-4 mb-4 space-y-2">
      {translations.map((translation) => (
        translation?.id ? (
          <TranslationPill
            key={translation.id}
            translation={translation}
            onCitationClick={onCitationClick}
            totalCitations={totalCitations}
            citations={citations}
            parentDisplayNumbers={parentDisplayNumbers}
          />
        ) : null
      ))}
    </div>
  );
}

export function BotMessage({
  message,
  researchSummary: researchSummaryProp,
  onAddressChatRedirect,
  onRegisterOpenAnswerReasoning,
}: BotMessageProps) {
  const [feedbackUi, setFeedbackUi] = useState(() => feedbackUiFromMessage(message));
  const isHelpfulClicked = feedbackUi.thumb_state === "helpful";
  const isUnhelpfulClicked = feedbackUi.thumb_state === "unhelpful";
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackDialogEntryPoint, setFeedbackDialogEntryPoint] = useState<FeedbackEntryPoint>("feedback");
  const [isResearchExpanded, setIsResearchExpanded] = useState(false);
  const [answerReasoningOpen, setAnswerReasoningOpen] = useState(false);
  const [answerReasoning, setAnswerReasoning] = useState<AnswerReasoning | null>(
    () => message.meta_data?.answer_reasoning ?? null
  );
  const [answerReasoningLoading, setAnswerReasoningLoading] = useState(false);
  const [answerReasoningError, setAnswerReasoningError] = useState<string | null>(null);
  const answerReasoningFetchingRef = useRef(false);
  const answerReasoningAbortRef = useRef<AbortController | null>(null);
  const { currentUser } = useUser();

  const researchSummary: ResearchSummary | null | undefined =
    researchSummaryProp || (message.meta_data?.research_summary as ResearchSummary | undefined);
  // const { fetchReference } = useReference();

  const { toast } = useToast();
  // Honeybadger is provided globally at runtime
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Honeybadger: any = (globalThis as any).Honeybadger;
  // Show analyzing status for new messages (created within last 10 seconds)
  const isNewMessage = (Date.now() - new Date(message.created_at).getTime()) < 10000;
  const isApology = /i['’]?m sorry/i.test(message.content || "");
  const [contradictionStatus, setContradictionStatus] = useState<{
    status: "analyzing" | "completed" | "error" | null;
    statusMessage?: string;
  }>({
    status: isNewMessage ? "analyzing" : null,
    statusMessage: isNewMessage ? "Code Doctor is reviewing sources automatically for potential conflicts..." : undefined
  });
  const [messageWithContradictions, setMessageWithContradictions] = useState(message);

  // Update local message state when prop changes
  useEffect(() => {
    setMessageWithContradictions(message);
  }, [message]);

  useEffect(() => {
    setFeedbackUi(feedbackUiFromMessage(message));
  }, [message]);

  const applyServerFeedbackState = (fs: MessageFeedbackState | undefined) => {
    if (!fs) return;
    setFeedbackUi({ thumb_state: fs.thumb_state, has_feedback: fs.has_feedback });
  };

  // Subscribe to contradiction status updates
  useEffect(() => {
    const subscription = createConsumer().subscriptions.create(
      {
        channel: "ChatChannel",
        chat_id: message.chat_id,
      },
      {
        received: (data: any) => {
          if (data.type === "minutes_saved" && data.message_id == message.id && data.minutes_saved) {
            // If this bot message is an apology, ignore time updates
            if (isApology) return;
            // Update meta_data locally so the UI shows the badge immediately
            (message as any).meta_data = {
              ...(message as any).meta_data,
              minutes_saved: data.minutes_saved,
              seconds_saved: typeof data.seconds_saved === "number" ? data.seconds_saved : (message as any).meta_data?.seconds_saved,
            };
          }
          if (data.type === "contradiction_status" && data.message_id == message.id) {
            setContradictionStatus({
              status: data.status,
              statusMessage: data.status_message,
            });

            // If completed with contradictions, update the message data
            if (data.status === "completed" && data.contradictions && data.contradictions.length > 0) {
              setMessageWithContradictions({
                ...message,
                suspected_document_contradictions: data.contradictions,
              });
            }
          }
        },
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [message.id, message.chat_id]);

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: "Message content copied!",
          description: "The response has been copied to your clipboard.",
          variant: "default",
        });
      },
      (err: Error) => {
        console.error("Failed to copy text: ", err);
      },
    );
  };

  const handleCitationClick = (key: string, contentForOrdering?: string): void => {
    if (!key) return;
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("citation:open", {
        detail: {
          messageId: message.id,
          key,
          ...(contentForOrdering ? { contentForOrdering } : {}),
        },
      })
    );
  };

  const citations = message.meta_data?.citation as CitationEntry[] | undefined;
  const citationLabels = citations?.map(citationEntryKey).filter(Boolean);
  const totalCitations = citations?.length ?? 0;
  const formattedContent = normalizeCitationLinks(
    sanitizeLLMOutput(message.content || ""),
    citations,
  );
  const markerStyle = detectCitationMarkerStyle(formattedContent, citationLabels);
  const displayNumbers = buildCitationDisplayNumbers(citations, formattedContent);
  const markdownComponents = buildMarkdownComponents(
    handleCitationClick,
    displayNumbers,
    markerStyle,
    citations,
  );
  const hasResearchSteps = researchSummary && researchSummary.phases.length > 0;

  useEffect(() => {
    setAnswerReasoning(message.meta_data?.answer_reasoning ?? null);
  }, [message.id, message.meta_data?.answer_reasoning]);

  useEffect(() => {
    return () => {
      answerReasoningAbortRef.current?.abort();
    };
  }, [message.id]);

  const handleAnswerReasoningOpenChange = (open: boolean) => {
    setAnswerReasoningOpen(open);
    if (!open) {
      answerReasoningAbortRef.current?.abort();
      answerReasoningAbortRef.current = null;
      answerReasoningFetchingRef.current = false;
      setAnswerReasoningLoading(false);
    }
  };

  const openAnswerReasoning = useCallback(async (): Promise<void> => {
    setAnswerReasoningOpen(true);
    if (answerReasoning) {
      setAnswerReasoningError(null);
      return;
    }
    if (answerReasoningFetchingRef.current) return;

    answerReasoningAbortRef.current?.abort();
    const controller = new AbortController();
    answerReasoningAbortRef.current = controller;

    answerReasoningFetchingRef.current = true;
    setAnswerReasoningError(null);
    setAnswerReasoningLoading(true);
    let softFailure = false;
    try {
      const url = `/api/v1/chats/${message.chat_id}/messages/${message.id}/answer_reasoning`;
      const fetchOptions: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        signal: controller.signal,
      };
      let pollIntervalMs = 2000;
      const deadline = Date.now() + 270_000;

      for (;;) {
        const response = await fetch(url, fetchOptions);
        if (controller.signal.aborted) return;

        if (response.status === 202) {
          // Unread 202 JSON is unused; cancel so the connection can be reused.
          void response.body?.cancel?.();
          if (Date.now() > deadline) {
            softFailure = true;
            throw new Error(`${ANSWER_REASONING_LABEL} is still generating. Please try again in a moment.`);
          }
          await sleep(pollIntervalMs, controller.signal);
          pollIntervalMs = Math.min(pollIntervalMs * 2, 10_000);
          continue;
        }

        if (!response.ok) {
          // GenerationFailed → 503 is an expected soft failure; do not page on copy changes.
          softFailure = response.status === 503;
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || `Failed to generate ${ANSWER_REASONING_LABEL}`);
        }

        const data = await response.json();
        if (!data?.answer_reasoning) {
          throw new Error(`${ANSWER_REASONING_LABEL} response was empty`);
        }
        setAnswerReasoning(data.answer_reasoning as AnswerReasoning);
        setAnswerReasoningError(null);
        break;
      }
    } catch (error) {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        return;
      }
      const messageText = error instanceof Error ? error.message : `Failed to generate ${ANSWER_REASONING_LABEL}`;
      setAnswerReasoningError(messageText);
      if (!softFailure) {
        Honeybadger?.notify?.(error);
      }
    } finally {
      answerReasoningFetchingRef.current = false;
      if (!controller.signal.aborted) {
        setAnswerReasoningLoading(false);
      }
      if (answerReasoningAbortRef.current === controller) {
        answerReasoningAbortRef.current = null;
      }
    }
  }, [answerReasoning, message.chat_id, message.id]);

  useEffect(() => {
    if (!onRegisterOpenAnswerReasoning) return;
    onRegisterOpenAnswerReasoning(openAnswerReasoning);
    return () => onRegisterOpenAnswerReasoning(null);
  }, [onRegisterOpenAnswerReasoning, openAnswerReasoning]);

  const header = (
    <>
      <div className="flex justify-between items-start mb-2">
        <div>
          {hasResearchSteps && (
            <button
              type="button"
              onClick={() => setIsResearchExpanded(!isResearchExpanded)}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${isResearchExpanded ? "rotate-90" : ""}`} />
              <span>See what Ordinal did</span>
            </button>
          )}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="message-content-copy-wrapper"
                onClick={() => copyToClipboard(formattedContent)}
                onKeyDown={(e) => (e.key === "Enter" ? copyToClipboard(formattedContent) : null)}
                tabIndex={0}
              >
                <Copy className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy response</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {hasResearchSteps && isResearchExpanded && (
        <div className="mb-4 pl-6 space-y-2 border-l-2 border-border">
          {researchSummary.phases.map((phase) => (
            <div key={phase.id}>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CircleCheckBig className="h-4 w-4 flex-shrink-0 text-green-500" />
                <span>{phase.label}</span>
              </div>
              {phase.tasks.length > 0 && (
                <div className="pl-6 space-y-1 mt-1">
                  {phase.tasks.map((task) => (
                    <div key={task.id} className="text-sm text-muted-foreground/80">
                      • {task.query}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  const afterContent = (
    <>
      <TranslationsSection
        translations={message.message_translations}
        onCitationClick={handleCitationClick}
        totalCitations={totalCitations}
        citations={citations}
        parentDisplayNumbers={displayNumbers}
      />
      {message.meta_data?.address_chat_redirect && onAddressChatRedirect ? (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50/80 p-4 dark:border-blue-900/50 dark:bg-blue-950/30">
          <p className="text-sm font-semibold text-foreground">{message.meta_data.address_chat_redirect.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{message.meta_data.address_chat_redirect.description}</p>
          <Button
            type="button"
            className="mt-3"
            onClick={() => onAddressChatRedirect(message.meta_data!.address_chat_redirect!)}
          >
            {message.meta_data.address_chat_redirect.cta_label}
          </Button>
        </div>
      ) : null}
      <AnswerReasoningPanel
        open={answerReasoningOpen}
        onOpenChange={handleAnswerReasoningOpenChange}
        messageId={message.id}
        citations={citations}
        contentForOrdering={formattedContent}
        answerReasoning={answerReasoning}
        isLoading={answerReasoningLoading}
        error={answerReasoningError}
        onRetry={() => {
          void openAnswerReasoning();
        }}
      />
      <Reasoning message={message as any} />
      <Citations message={message as any} citationContent={formattedContent} />
      {currentUser?.code_doctor && (
        <Contradictions
          message={messageWithContradictions}
          scanningStatus={contradictionStatus.status}
        />
      )}
    </>
  );

  const footer = (
    <div className="flex items-center gap-1 justify-between">
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8 lg:h-14 lg:w-14 flex items-center bg-gray-200 dark:bg-gray-800">
          <OrdinalIcon fill="black" />
        </Avatar>
        <relative-time datetime={message.created_at} className="text-sm text-muted-foreground" />
      </div>
      <div className="flex items-center gap-3">
        {message.meta_data?.minutes_saved && !isApology ? (
          <div suppressHydrationWarning>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:border-blue-800/50 dark:bg-blue-900/30 dark:text-blue-300">
                    <Timer className="h-3 w-3" />
                    {(() => {
                      const mins = message.meta_data?.minutes_saved ?? 0;
                      const secs = message.meta_data?.seconds_saved ?? 0;
                      const padded = secs.toString().padStart(2, "0");
                      return `Est. ${mins}m ${padded}s saved with Ordinal`;
                    })()}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    We calculate time saved using input from city staff, expert advisors, and real usage data from
                    thousands of chats.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : null}
        <FeedbackActions
          isHelpfulClicked={isHelpfulClicked}
          isUnhelpfulClicked={isUnhelpfulClicked}
          hasFeedbackSubmission={feedbackUi.has_feedback}
          onFeedback={() => {
            setFeedbackDialogEntryPoint("feedback");
            setIsFeedbackDialogOpen(true);
          }}
          onHelpful={async () => {
            try {
              if (isHelpfulClicked) {
                const data = await deleteMessageEvaluation(message.id);
                applyServerFeedbackState(data.feedback_state);
              } else {
                const data = await submitMessageEvaluation(message.id, { evaluation: "helpful" });
                applyServerFeedbackState(data.feedback_state);
              }
            } catch (error) {
              console.error("Failed to submit feedback:", error);
              Honeybadger && Honeybadger.notify && Honeybadger.notify(error);
            }
          }}
          onUnhelpful={async () => {
            try {
              if (isUnhelpfulClicked) {
                const data = await deleteMessageEvaluation(message.id);
                applyServerFeedbackState(data.feedback_state);
              } else {
                setFeedbackDialogEntryPoint("thumbs_down");
                setIsFeedbackDialogOpen(true);
              }
            } catch (error) {
              console.error("Failed to submit feedback:", error);
              Honeybadger && Honeybadger.notify && Honeybadger.notify(error);
            }
          }}
        />
      </div>
      <FeedbackDialog
        open={isFeedbackDialogOpen}
        onOpenChange={setIsFeedbackDialogOpen}
        entryPoint={feedbackDialogEntryPoint}
        onSubmit={async ({ reasonCode, detail }) => {
          const res =
            feedbackDialogEntryPoint === "thumbs_down"
              ? await submitMessageEvaluation(message.id, { evaluation: "unhelpful", reasonCode, detail })
              : await submitStructuredMessageFeedback(message.id, { reasonCode, detail });
          applyServerFeedbackState(res.feedback_state);
        }}
      />
    </div>
  );

  return (
    <ChatAssistantBubble
      content={formattedContent}
      createdAt={message.created_at}
      sanitize={false}
      header={header}
      afterContent={afterContent}
      footer={footer}
      markdownComponents={markdownComponents}
      urlTransform={urlTransform}
    />
  );
}
